import {
  Avatar, AvatarFallback,
  Badge,
  Button,
  Card, CardContent, CardHeader, CardTitle, CardDescription, CardFooter,
  DiagramFrame,
  Eyebrow,
  EvidenceCard,
  GateStrip,
  Input,
  JourneyStepper,
  Progress,
  Separator,
  Stat,
  Tabs, TabsContent, TabsList, TabsTrigger,
  Tag,
  Textarea,
  Tooltip, TooltipContent, TooltipProvider, TooltipTrigger,
  type Gate,
  type JourneyStep,
} from '@/components/foundry';
import { ArrowRight, Bell, Search, Sparkles } from 'lucide-react';
import PostItModulesPreview from './PostItModulesPreview';

interface Props { tab: 'tokens' | 'primitives' | 'composites'; }

export default function DesignSystemPage({ tab }: Props) {
  if (tab === 'tokens') return <TokensPanel />;
  if (tab === 'primitives') return <PrimitivesPanel />;
  return <CompositesPanel />;
}

// ── Tokens ─────────────────────────────────────────────────────────────

function TokensPanel() {
  const surfaces = [
    { name: 'bg', token: '--fd-bg' },
    { name: 'surface-1', token: '--fd-surface-1' },
    { name: 'surface-2', token: '--fd-surface-2' },
    { name: 'surface-3', token: '--fd-surface-3' },
  ] as const;
  const text = [
    { name: 'text', token: '--fd-text' },
    { name: 'text-muted', token: '--fd-text-muted' },
    { name: 'text-subtle', token: '--fd-text-subtle' },
  ] as const;
  const accents = [
    { name: 'accent', token: '--fd-accent' },
    { name: 'accent-line', token: '--fd-accent-line' },
    { name: 'accent-soft', token: '--fd-accent-soft' },
  ] as const;
  const status = [
    { name: 'ok', token: '--fd-ok' },
    { name: 'warn', token: '--fd-warn' },
    { name: 'bad', token: '--fd-bad' },
  ] as const;

  return (
    <div className="grid gap-10">
      <SectionHead eyebrow="Foundry · Tokens" title="Design tokens" sub="Single oklch token set, additive on top of existing legacy tokens. Use bg-fd-* / text-fd-* / border-fd-* utilities." />

      <SwatchGroup title="Surfaces" items={surfaces.map(s => ({ ...s, value: s.token }))} />
      <SwatchGroup title="Text" items={text.map(s => ({ ...s, value: s.token }))} variant="text" />
      <SwatchGroup title="Accent · sodium amber" items={accents.map(s => ({ ...s, value: s.token }))} />
      <SwatchGroup title="Status (operational only)" items={status.map(s => ({ ...s, value: s.token }))} />

      <Card>
        <CardHeader>
          <CardTitle>Type scale</CardTitle>
          <Eyebrow>GEIST VARIABLE · GEIST MONO VARIABLE</Eyebrow>
        </CardHeader>
        <CardContent className="grid gap-3">
          <TypeRow label="display-xl" cls="text-[44px] leading-[48px] font-medium tracking-[-0.025em]">Build your AI-native business.</TypeRow>
          <TypeRow label="display-lg" cls="text-[32px] leading-[36px] font-medium tracking-[-0.02em]">The Game Plan</TypeRow>
          <TypeRow label="heading"    cls="text-[18px] leading-[24px] font-medium tracking-[-0.01em]">Conversion Path · Warm Bridge build</TypeRow>
          <TypeRow label="title"      cls="text-[14px] leading-[20px] font-semibold">Today's Lab pulse</TypeRow>
          <TypeRow label="body"       cls="text-[14px] leading-[22px]">You've locked the offer and AI Belief Bridge. Next gate writes the page that converts your invited list.</TypeRow>
          <TypeRow label="small"      cls="text-[12px] leading-[18px] text-fd-text-muted">Cohort 04 is shipping warm bridges at 2× the conversion of cohort 03.</TypeRow>
          <TypeRow label="mono-eyebrow" cls="font-geist-mono text-[10.5px] leading-[14px] uppercase tracking-[0.06em] text-fd-text-subtle">DAY 17 · MONTH ONE · ACQUISITION</TypeRow>
        </CardContent>
      </Card>
    </div>
  );
}

function SwatchGroup({
  title, items, variant = 'surface',
}: {
  title: string;
  items: ReadonlyArray<{ name: string; token: string; value: string }>;
  variant?: 'surface' | 'text';
}) {
  return (
    <section className="grid gap-3">
      <h3 className="font-geist text-[13px] font-semibold tracking-[-0.01em]">{title}</h3>
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4">
        {items.map((s) => (
          <div key={s.name} className="rounded-fd border border-fd-border bg-fd-surface-1 overflow-hidden">
            <div
              className="h-16 w-full"
              style={
                variant === 'text'
                  ? { background: 'var(--fd-surface-2)', display: 'grid', placeItems: 'center', color: `var(${s.token})`, fontFamily: 'Geist Variable', fontSize: 16 }
                  : { background: `var(${s.token})` }
              }
            >
              {variant === 'text' ? 'Aa Bb Cc' : null}
            </div>
            <div className="px-3 py-2.5 grid gap-0.5">
              <div className="font-geist text-[12.5px] font-medium">{s.name}</div>
              <code className="font-geist-mono text-[10.5px] text-fd-text-subtle">{s.value}</code>
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}

function TypeRow({ label, cls, children }: { label: string; cls: string; children: React.ReactNode }) {
  return (
    <div className="grid grid-cols-[120px_1fr] items-baseline gap-6 border-b border-fd-border last:border-0 pb-3 last:pb-0">
      <code className="font-geist-mono text-[10.5px] text-fd-text-subtle uppercase tracking-[0.06em]">{label}</code>
      <div className={cls}>{children}</div>
    </div>
  );
}

// ── Primitives ─────────────────────────────────────────────────────────

function PrimitivesPanel() {
  return (
    <div className="grid gap-10">
      <SectionHead eyebrow="Foundry · Primitives" title="Buttons, inputs, badges, tags" sub="shadcn-style API on Foundry tokens. Importable from @/components/foundry." />

      <Card>
        <CardHeader>
          <CardTitle>Button</CardTitle>
          <Eyebrow>6 VARIANTS · 5 SIZES</Eyebrow>
        </CardHeader>
        <CardContent className="grid gap-5">
          <Row label="variants">
            <Button variant="primary">Resume next move <ArrowRight /></Button>
            <Button>Open in workspace</Button>
            <Button variant="outline">Adopt / Ignore</Button>
            <Button variant="ghost">View week plan</Button>
            <Button variant="link">Open report →</Button>
            <Button variant="destructive">Reset session</Button>
          </Row>
          <Row label="sizes">
            <Button size="sm" variant="primary">Small</Button>
            <Button variant="primary">Default</Button>
            <Button size="lg" variant="primary">Large</Button>
            <Button size="icon" variant="default" aria-label="bell"><Bell /></Button>
            <Button size="icon-sm" variant="ghost" aria-label="search"><Search /></Button>
          </Row>
          <Row label="state">
            <Button variant="primary" disabled>Disabled</Button>
            <Button variant="primary"><Sparkles /> With icon</Button>
          </Row>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Inputs</CardTitle>
          <Eyebrow>FOCUS RING USES ACCENT</Eyebrow>
        </CardHeader>
        <CardContent className="grid gap-4 max-w-md">
          <Input placeholder="Jump to anything…" />
          <Input placeholder="Disabled input" disabled />
          <Textarea placeholder="Draft your reply to Tom…" rows={3} />
        </CardContent>
      </Card>

      <Card>
        <CardHeader><CardTitle>Badge & Tag</CardTitle></CardHeader>
        <CardContent className="grid gap-4">
          <Row label="badge"><Badge>Default</Badge><Badge variant="accent">Active</Badge><Badge variant="ok">Passed</Badge><Badge variant="warn">Warning</Badge><Badge variant="bad">Blocked</Badge><Badge variant="outline">Outline</Badge></Row>
          <Row label="tag (mono)"><Tag>Muted</Tag><Tag variant="accent">Active</Tag><Tag variant="ok">Live</Tag><Tag variant="warn">Caution</Tag><Tag variant="bad">Blocked</Tag><Tag variant="outline">Outline</Tag></Row>
        </CardContent>
      </Card>

      <Card>
        <CardHeader><CardTitle>Tabs · Avatar · Progress · Tooltip · Separator</CardTitle></CardHeader>
        <CardContent className="grid gap-6">
          <Tabs defaultValue="ask">
            <TabsList>
              <TabsTrigger value="ask">Ask the Lab</TabsTrigger>
              <TabsTrigger value="community">Community</TabsTrigger>
              <TabsTrigger value="tom">Ask Tom</TabsTrigger>
            </TabsList>
            <TabsContent value="ask"><Card><CardContent className="pt-5 text-fd-text-muted text-[13px]">Private AI-first support.</CardContent></Card></TabsContent>
            <TabsContent value="community"><Card><CardContent className="pt-5 text-fd-text-muted text-[13px]">Curated searchable shared answers.</CardContent></Card></TabsContent>
            <TabsContent value="tom"><Card><CardContent className="pt-5 text-fd-text-muted text-[13px]">Selective batched async founder escalation.</CardContent></Card></TabsContent>
          </Tabs>

          <Row label="avatar">
            <Avatar size="sm"><AvatarFallback>PJ</AvatarFallback></Avatar>
            <Avatar><AvatarFallback>PJ</AvatarFallback></Avatar>
            <Avatar size="lg"><AvatarFallback>TR</AvatarFallback></Avatar>
          </Row>

          <Row label="progress">
            <div className="w-64 grid gap-1">
              <Progress value={64} />
              <Eyebrow>SUBSCRIPTION · 19 OF 30 DAYS</Eyebrow>
            </div>
          </Row>

          <Row label="tooltip">
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild><Button variant="ghost" size="icon"><Bell /></Button></TooltipTrigger>
                <TooltipContent>2 new memos from Tom</TooltipContent>
              </Tooltip>
            </TooltipProvider>
          </Row>

          <div className="grid gap-2">
            <Eyebrow>SEPARATOR</Eyebrow>
            <Separator />
            <Separator strong />
            <Separator dashed strong />
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

function Row({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="grid grid-cols-[120px_1fr] items-center gap-6">
      <code className="font-geist-mono text-[10.5px] text-fd-text-subtle uppercase tracking-[0.06em]">{label}</code>
      <div className="flex flex-wrap items-center gap-2.5">{children}</div>
    </div>
  );
}

// ── Composites ─────────────────────────────────────────────────────────

function CompositesPanel() {
  const journey: JourneyStep[] = [
    { label: 'The Shift',           sub: 'Locked · Day 1–2',     state: 'done' },
    { label: 'The Game Plan',       sub: 'Locked · Day 4',       state: 'done' },
    { label: 'Engine build',        sub: '3 of 6 gates passed',  state: 'current' },
    { label: 'First working version', sub: 'Target · Day 28',    state: 'upcoming' },
  ];

  const gates: Gate[] = [
    { num: '01', label: 'Offer Lock',         state: 'done' },
    { num: '02', label: 'AI Belief Bridge',   state: 'done' },
    { num: '03', label: 'Audience map',       state: 'done' },
    { num: '04', label: 'Conversion Path',    state: 'current' },
    { num: '05', label: 'Welcome Path',       state: 'open' },
    { num: '06', label: 'Readiness verdict',  state: 'open' },
  ];

  return (
    <div className="grid gap-10">
      <SectionHead eyebrow="Foundry · Composites" title="Brand-specific patterns" sub="JourneyStepper, GateStrip, EvidenceCard, Stat, DiagramFrame — used everywhere from Command Centre to The Game Plan to Build Room." />

      {/* Post-it module proposal — variation A. Preview-only, not yet promoted to a Foundry primitive. */}
      <PostItModulesPreview />

      <Card>
        <CardHeader>
          <CardTitle>Journey stepper</CardTitle>
          <Eyebrow>CHECKPOINT 3 OF 4 · ENGINE BUILD</Eyebrow>
        </CardHeader>
        <CardContent>
          <JourneyStepper steps={journey} />
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Gate strip</CardTitle>
          <Eyebrow>HORIZONTAL · ENGINE WORKSPACE</Eyebrow>
        </CardHeader>
        <CardContent>
          <GateStrip gates={gates} />
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Evidence card</CardTitle>
          <Eyebrow>WORKSHOP-NOTE STYLE · INSIDE CONTENT</Eyebrow>
        </CardHeader>
        <CardContent className="grid gap-4 max-w-2xl">
          <EvidenceCard
            kind="locked"
            eyebrow="EVIDENCE LOCKED · GATE 03 · BENEFIT-OR-BREAK"
            actions={<>
              <Button variant="ghost" size="sm">Edit premise</Button>
              <Button variant="ghost" size="sm">Show how I'll use this →</Button>
            </>}
          >
            Static membership content is <b>at risk</b>. Recommended next gate: Steering Layer — define what your method does that a generalist model can't.
          </EvidenceCard>

          <EvidenceCard kind="verdict" title={<span className="text-[15px] font-medium">AI Acquisition Engine</span>}>
            Based on 3 locked premises: PJPT proof spine, $1,800/mo paid traffic running cold, and 11h/wk burned on calls. Confidence climbs as gates lock.
          </EvidenceCard>
        </CardContent>
      </Card>

      <Card>
        <CardHeader><CardTitle>Stat</CardTitle></CardHeader>
        <CardContent className="grid grid-cols-3 gap-6">
          <Stat label="Cohort vs your week 1" value="3.4×" delta="▲ 0.6 wk-on-wk" />
          <Stat label="Bridge replies"        value="4.2" unit="%" delta="▼ 0.8" deltaTone="bad" />
          <Stat label="Gates passed"          value="3" unit="of 6" delta="on track" size="sm" />
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Diagram frame</CardTitle>
          <Eyebrow>WORKSHOP-NOTE INSIDE CALM CHROME</Eyebrow>
        </CardHeader>
        <CardContent>
          <DiagramFrame caption="BENEFIT OR BREAK · YOUR TWO BUSINESSES ON ONE AXIS">
            <svg viewBox="0 0 600 180" xmlns="http://www.w3.org/2000/svg">
              <line x1="40" y1="150" x2="560" y2="150" stroke="var(--fd-border-strong)" strokeWidth="1"/>
              <text x="40" y="172" fontFamily="Geist Mono Variable, monospace" fontSize="10" fill="var(--fd-text-subtle)" letterSpacing="0.06em">TODAY</text>
              <text x="540" y="172" fontFamily="Geist Mono Variable, monospace" fontSize="10" fill="var(--fd-text-subtle)" letterSpacing="0.06em">+12 MO</text>
              <path d="M40 130 Q 200 110 300 80 T 560 30" stroke="var(--fd-ok)" strokeWidth="2" fill="none" strokeDasharray="4 4"/>
              <text x="380" y="55" fontFamily="Geist Variable" fontSize="12" fill="var(--fd-ok)">High-ticket coaching · benefits</text>
              <path d="M40 80 Q 200 100 300 120 T 560 150" stroke="var(--fd-bad)" strokeWidth="2" fill="none"/>
              <text x="380" y="138" fontFamily="Geist Variable" fontSize="12" fill="var(--fd-bad)">Static membership · breaks</text>
              <line x1="200" y1="20" x2="200" y2="160" stroke="var(--fd-accent-line)" strokeWidth="1" strokeDasharray="2 4"/>
              <circle cx="200" cy="98" r="5" fill="var(--fd-accent)"/>
              <text x="208" y="22" fontFamily="Geist Mono Variable, monospace" fontSize="10" fill="var(--fd-accent)" letterSpacing="0.06em">YOU · TODAY</text>
            </svg>
          </DiagramFrame>
        </CardContent>
      </Card>

      <Card>
        <CardHeader><CardTitle>Composition · Today's Lab pulse</CardTitle></CardHeader>
        <CardContent className="grid gap-4">
          <div className="grid gap-3 max-w-2xl">
            <div className="flex items-start gap-3 rounded-fd border border-fd-border bg-fd-surface-1 p-4">
              <Avatar><AvatarFallback>!</AvatarFallback></Avatar>
              <div className="flex-1 grid gap-1">
                <div className="flex items-center justify-between gap-3">
                  <h4 className="font-geist text-[13px] font-medium">Bridge replies are flat</h4>
                  <Tag variant="accent">Bottleneck</Tag>
                </div>
                <p className="text-[13px] text-fd-text-muted leading-relaxed">
                  Open rate 51% but reply rate 4.2%. The Lab thinks your bridge headline is too abstract.
                  Drop in 1 PJPT proof line and re-test by Friday.
                </p>
                <Eyebrow>SIGNAL · 2H AGO</Eyebrow>
              </div>
            </div>
          </div>
        </CardContent>
        <CardFooter>
          <Button variant="ghost" size="sm">Open the Lab</Button>
          <Button variant="primary" size="sm">Apply suggested move</Button>
        </CardFooter>
      </Card>

      <Card>
        <CardHeader><CardTitle>Card variants</CardTitle></CardHeader>
        <CardContent className="grid grid-cols-2 gap-4">
          <Card>
            <CardHeader>
              <div className="grid gap-1">
                <CardTitle>Latest artifact</CardTitle>
                <Eyebrow>UPDATED 2H AGO</Eyebrow>
              </div>
              <Tag variant="accent">v2</Tag>
            </CardHeader>
            <CardContent>
              <CardDescription>
                "Your model breaks if AI gets 3× smarter and your offer stays static. The fastest fix is a no-call acquisition path with Bridge personalization layered on PJPT-style proof."
              </CardDescription>
            </CardContent>
          </Card>
          <Card>
            <CardHeader><CardTitle>From the founder</CardTitle><Eyebrow>26 APR</Eyebrow></CardHeader>
            <CardContent>
              <CardDescription>
                Cohort 04 is shipping warm bridges at 2× the conversion of cohort 03. Three patterns in this week's Lab memo — landing Friday with a 7-min walkthrough.
              </CardDescription>
            </CardContent>
            <CardFooter>
              <Button variant="ghost" size="sm">Open memo</Button>
              <Button variant="ghost" size="sm">Adopt / Ignore</Button>
            </CardFooter>
          </Card>
        </CardContent>
      </Card>
    </div>
  );
}

// ── Section head ───────────────────────────────────────────────────────

function SectionHead({ eyebrow, title, sub }: { eyebrow: string; title: string; sub: string }) {
  return (
    <header className="grid gap-2">
      <Eyebrow>{eyebrow}</Eyebrow>
      <h2 className="font-geist text-[24px] font-medium tracking-[-0.02em]">{title}</h2>
      <p className="max-w-2xl text-[13.5px] text-fd-text-muted leading-relaxed">{sub}</p>
    </header>
  );
}
